import { useEffect, useRef, useState } from "react";
import { BoardCard } from "../components/BoardCard";
import { BoardProductCard } from "../components/BoardProductCard";
import { Composer, type ComposerHandle } from "../components/Composer";
import { EmptyState } from "../components/EmptyState";
import { classifyInput, discoverProduct, extractUrls, getHome, sendChat } from "../lib/api";
import { navigate } from "../lib/router";
import type { Board, DiscoveryProduct } from "../lib/types";

const HEADINGS = [
  "What are you lusting for?",
  "What's the latest obsession?",
  "Found something good?",
  "Show me something beautiful.",
  "What's on your mind?",
  "Spotted something?",
  "What's hot right now?",
];

export function DiscoveryRoute() {
  const [boards, setBoards] = useState<Board[]>([]);
  const [finds, setFinds] = useState<DiscoveryProduct[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [heading] = useState(() => HEADINGS[Math.floor(Math.random() * HEADINGS.length)]!);
  const ref = useRef<ComposerHandle>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getHome();
      if (cancelled || !data) {
        return;
      }
      setBoards(data.boards);
      setFinds(data.finds);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (message: string) => {
    const composer = ref.current;
    if (!composer) {
      return;
    }

    const urls = extractUrls(message);

    if (urls.length > 0) {
      try {
        new URL(urls[0]!);
      } catch {
        setFeedback("Please enter a valid URL");
        return;
      }
    }

    setFeedback(null);
    composer.setSending(true);

    if (urls.length > 0) {
      const result = await discoverProduct({ url: urls[0]! });
      composer.setSending(false);
      if (!result.ok) {
        return;
      }
      composer.setValue("");
      navigate(`/finds/${result.data.find.id}`);
    } else {
      const classification = await classifyInput(message);
      if (!classification.ok) {
        composer.setSending(false);
        return;
      }
      if (classification.data.type === "search") {
        composer.setSending(false);
        composer.setValue("");
        navigate(`/search?q=${encodeURIComponent(message)}`);
      } else {
        const result = await sendChat({ message, boardId: null });
        composer.setSending(false);
        if (!result.ok) {
          return;
        }
        composer.setValue("");
        navigate(`/boards/${result.data.board.id}`);
      }
    }
  };

  return (
    <section
      id="discovery-view"
      className="w-full px-4 pb-4 grid gap-[1.8rem] pt-[2.4rem] flex-1 min-h-0 overflow-auto content-start"
    >
      <div className="grid justify-items-center gap-[1.15rem] text-center pt-4">
        <h1 className="m-0 font-bold tracking-[-0.05em] leading-[0.96] text-[clamp(1.9rem,5vw,3.4rem)]">
          {heading}
        </h1>
        <div className="w-full max-w-[760px]">
          {feedback && <p className="text-sm text-foreground/50 mb-2">{feedback}</p>}
          <Composer
            ref={ref}
            textareaId="home-chat-input"
            buttonId="home-send-btn"
            placeholder="Paste a URL, search for finds, or ask a question"
            buttonLabel="Go"
            onSubmit={submit}
          />
        </div>
      </div>

      <section className="grid gap-4 pt-2 border-t border-line">
        <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
          Recent Finds
        </h2>
        <div
          id="discovery-grid"
          className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4"
        >
          {finds.length === 0 ? (
            <EmptyState text="Finds will appear here." />
          ) : (
            finds.map((find) => (
              <BoardProductCard
                key={find.id}
                product={find}
              />
            ))
          )}
        </div>
      </section>

      {boards.length > 0 && (
        <section className="grid gap-4 pt-2 border-t border-line">
          <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
            Recent Boards
          </h2>
          <div
            id="home-board-list"
            className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4"
          >
            {boards.map((board) => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        </section>
      )}
    </section>
  );
}
